import { resetAnalyticsUser } from '@psl/functions/analytics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import * as Device from 'expo-device';
import * as SecureStore from 'expo-secure-store';
import { useEffect } from 'react';
import { Platform } from 'react-native';
import { combineLatest, defer, EMPTY, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import getUserAgent from '../../flows/purchase/functions/userAgent';
import { OnboardingStateManager } from '../../flows/user/functions/onboardingState';
import { LoginSession } from '../../models/ClientModels';
import { makeDebug } from '../../utils/makeDebug';
import { shareReplay } from '../../utils/rxExtensions';
import { sleep } from '../../utils/utils';
import { code, current } from '../../version.json';
import { currentUserId, currentUserId$ } from '../auth/utils';
import { isDebugEnabled } from '../debugging';
import { clearAsyncStorageExceptDebugFlags } from '../debugging/debugFlags';
import { isSimulator } from '../deviceInfo/isSimulator';
import { clearSavedCampaignId } from '../dynamic-links/campaignTracker';
import {
  auth,
  fieldValueDelete,
  firestore,
  serverNow,
} from '../firebase/firebaseAccess';
import { useObservable } from '../hooks/useObservable';
import { usePersistFn } from '../hooks/usePersistFn';
import { notificationState$ } from '../notification/permissions';

const installationIdKey = 'com.popshop.installation.key';
const sessionIdKey = 'com.popshop.session.key';

const debug = makeDebug('session-tracker', isDebugEnabled('sessionTracker'));

const randomIdInLocalStorage = async (): Promise<string> => {
  const result = await AsyncStorage.getItem(installationIdKey);
  if (result != null) {
    return result;
  }
  const installId = uuid();
  await AsyncStorage.setItem(installationIdKey, installId);
  return installId;
};

export const getDeviceID = async (): Promise<string> => {
  if (Platform.OS === 'android') {
    return Application.androidId ?? (await randomIdInLocalStorage());
  }
  if (Platform.OS === 'ios') {
    if (!(await SecureStore.isAvailableAsync())) {
      return await randomIdInLocalStorage();
    }
    const existing = await SecureStore.getItemAsync(installationIdKey);
    if (existing != null) {
      return existing;
    }
    const installId = uuid();
    await SecureStore.setItemAsync(installationIdKey, installId);
    return installId;
  }
  return await randomIdInLocalStorage();
};

export const deviceId$ = defer(() => from(getDeviceID())).pipe(shareReplay(1));

export const useDeviceId = () =>
  useObservable(() => deviceId$, 'current-device-id');

export const clearSessionId = async () => {
  await AsyncStorage.removeItem(sessionIdKey);
};

export const getSessionId = async (): Promise<{
  id: string;
  newSession: boolean;
}> => {
  const existing = await AsyncStorage.getItem(sessionIdKey);
  if (existing != null) {
    return {
      id: existing,
      newSession: false,
    };
  }
  const sessionId = uuid();
  await AsyncStorage.setItem(sessionIdKey, sessionId);
  return {
    newSession: true,
    id: sessionId,
  };
};

export const useSessionId = (defaultValue?: string) => {
  return useObservable(
    () => from(getSessionId()).pipe(map(p => p.id)),
    'current-session-id',
    defaultValue,
  );
};

/**
 * this function just expires only session, real logout implemented by other functions
 */
const logoutSession = async () => {
  const { id, newSession } = await getSessionId();

  if (newSession) {
    return;
  }

  const sessionData: Partial<LoginSession> = {
    loggedIn: false,
    loggedOutAt: serverNow,
  };

  await firestore()
    .collection('privateProfiles')
    .doc(currentUserId()!)
    .collection('sessions')
    .doc(id)
    .set(sessionData, { merge: true });
};

export async function logout(): Promise<void> {
  debug('logging out user... ');
  await logoutSession();
  await auth.signOut();
  resetAnalyticsUser();
  OnboardingStateManager.shared.reset();
  clearAsyncStorageExceptDebugFlags().catch(() => null);
  clearSavedCampaignId();
}

export const useLogout = (): ((options?: {
  safeForNavigate?: boolean;
}) => Promise<void>) => {
  return usePersistFn(async options => {
    const { safeForNavigate = false } = options ?? {};

    if (currentUserId() != null) {
      await logout();
      if (safeForNavigate) {
        await sleep(200);
      }
    }
  });
};

const sessionTracker = combineLatest([
  currentUserId$,
  from(getDeviceID()),
  notificationState$,
]).pipe(
  switchMap(([uid, deviceId, pushToken]) => {
    const deviceModel =
      Device.modelName ?? Device.deviceName ?? Device.manufacturer!;
    if (pushToken.type === 'pending') {
      return EMPTY;
    }
    if (uid == null) {
      return from(clearSessionId());
    }
    return from(getSessionId()).pipe(
      switchMap(session => {
        const sessionData: Partial<LoginSession> = {
          platform: Platform.OS,
          deviceId,
          hasPushToken: pushToken.type === 'granted',
          pushToken:
            pushToken.type === 'granted' ? pushToken.token : fieldValueDelete,
          deviceModel,
          loggedIn: true,
          lastUsed: serverNow,
          lastUsedAppId:
            Application.applicationId ?? `web: ${window.location.host}`,
          lastUsedAppVersion: `native: ${
            Application.nativeApplicationVersion
          }-${
            Application.nativeBuildVersion ?? 'unknown_native'
          } js: ${code}-${current.slice(0, 6)}`,
          userAgent: getUserAgent(),
          ...({
            isSimulator: isSimulator() ? true : undefined,
          } as any),
        };
        if (session.newSession) {
          sessionData.loggedInAt = serverNow;
        }
        debug('save user session data: %O', sessionData);
        return from(
          firestore()
            .collection('privateProfiles')
            .doc(uid)
            .collection('sessions')
            .doc(session.id)
            .set(sessionData, { merge: true }),
        );
      }),
    );
  }),
);

export const useTrackSession = () => {
  useEffect(() => {
    const sub = sessionTracker.subscribe();

    return () => {
      sub.unsubscribe();
    };
  }, []);
};
